import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useIntake } from "../context/IntakeContext";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden pb-[0.08em] -mb-[0.08em] ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const intake = useIntake();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <section
      id="why"
      ref={ref}
      className="relative pt-[68px] overflow-hidden bg-[#081226]"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 75% 55%, rgba(37,99,235,0.25), transparent 65%), radial-gradient(ellipse 60% 50% at 15% 10%, rgba(37,99,235,0.12), transparent 60%)",
        }}
      />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10 sm:pt-20 lg:pt-24 lg:pb-0">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 lg:pb-24">
            <h1
              data-testid="hero-headline"
              className="font-display font-semibold text-[13vw] sm:text-6xl lg:text-7xl xl:text-[5rem] leading-[1.02] tracking-tight text-white"
            >
              <MaskedLine delay={0.15}>Your rights.</MaskedLine>
              <MaskedLine delay={0.27}>Your voice.</MaskedLine>
              <MaskedLine delay={0.39}>
                <span className="text-[#3B82F6]">Your day in court.</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-7 text-lg sm:text-xl font-semibold text-white"
            >
              Because the cost of a lawyer shouldn&rsquo;t cost you justice.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.82 }}
              className="mt-3 text-base sm:text-lg text-[#9FB3D9] leading-relaxed max-w-xl"
            >
              Tools to help you prepare and pursue your civil case without a
              lawyer — across all 50 states.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.95 }}
              className="mt-10"
            >
              <button
                data-testid="start-case-button"
                onClick={() => intake.open("letter")}
                className="group inline-flex items-center gap-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold text-lg pl-8 pr-2.5 py-2.5 rounded-full transition-colors duration-300 shadow-[0_18px_40px_-12px_rgba(37,99,235,0.55)]"
              >
                Start Your Case
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-white text-[#2563EB] transition-transform duration-300 group-hover:rotate-[-35deg]">
                  <ArrowRight size={20} strokeWidth={2.5} />
                </span>
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <motion.img
              style={{ y: imgY }}
              src="/hero-illustration.png"
              alt="Case folder with complaint, checklist and summons documents"
              data-testid="hero-illustration"
              className="w-full max-w-lg mx-auto lg:max-w-none lg:-mb-2 [mask-image:radial-gradient(ellipse_90%_85%_at_50%_50%,black_55%,transparent_100%)]"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
